import { Injectable, inject } from '@angular/core';
import {
  Database,
  onValue,
  push,
  query,
  ref,
  set,
  limitToLast,
  limitToFirst,
  remove,
  onChildAdded,
  onChildRemoved,
  onChildChanged,
  update,
  orderByKey,
  startAfter as firebaseStartAfter,
} from '@angular/fire/database';
import { Auth } from '@angular/fire/auth';
import { Notifications } from '../interfaces/base-interface';
import ShortUniqueId from 'short-unique-id';
import humanizeDuration from 'humanize-duration';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private rtdb = inject(Database);
  private auth = inject(Auth);
  private okToDing: boolean = true;
  public myNotifications: Notifications[] = [];
  public unreadCount: number = 0;
  private notificationsWatcher: any;
  private notificationsRemovedWatcher: any;
  private notificationsChangedWatcher: any;

  constructor() {}

  sendNotification(
    toUserID: string = 'testuser',
    message = 'test message',
    msgType: 'normal' | 'success' | 'warning' | 'error' = 'normal',
    channels: ('realtime-db' | 'fcm' | 'email' | 'sms')[] = ['realtime-db'],
    data: any = {},
    callToActionLink?: string
    ) {
        const nowDate = new Date();
        const uid = new ShortUniqueId({ length: 10, dictionary: 'alphanum_upper' });
        const notificationID = uid.rnd();
        const notificationsRef = ref(this.rtdb, 'notifications/' + toUserID);
        const newNotificationRef = push(notificationsRef);
        set(newNotificationRef, {
        notificationID: notificationID,
        message: message,
        msgType: msgType,
        status: 'new',
        updateTimestamp: nowDate.getTime(),
        channels: channels,
        recipientId: toUserID,
        data: data,
        isRead: false,
        callToActionLink: callToActionLink || null,
        }).then(
        () => {
            console.log('sent notification');
        },
        (err) => {
            console.log('error.while sending notification', err);
        }
        );
    }

  readMyNotifications(userID: string) {
    console.log('[RTDB-DEBUG] read my notifications', userID);
    console.log('[RTDB-DEBUG] Current auth user:', this.auth.currentUser?.uid);
    console.log('[RTDB-DEBUG] Auth user email:', this.auth.currentUser?.email);
    console.log('[RTDB-DEBUG] Firebase Database instance:', this.rtdb);
    console.log('[RTDB-DEBUG] Firebase Database app:', (this.rtdb as any).app?.options);
    const myNotificationsRef = ref(this.rtdb, 'notifications/' + userID);
    console.log('[RTDB-DEBUG] Created reference to path:', 'notifications/' + userID);
    const recentNotificationsRef = query(myNotificationsRef, limitToLast(10));
    let firstTime = true;
    let gotNotifications = true;
    this.myNotifications = [];

    // Listen for new notifications
    console.log('[RTDB-DEBUG] Setting up onChildAdded listener for notifications...');
    
    // First, let's try to read the data once to check if we have access
    console.log('[RTDB-DEBUG] About to call onValue...');
    const unsubscribe = onValue(myNotificationsRef, (snapshot) => {
      console.log('[RTDB-DEBUG] onValue callback fired!');
      console.log('[RTDB-DEBUG] onValue check - snapshot exists?', snapshot.exists());
      if (snapshot.exists()) {
        const data = snapshot.val();
        const count = Object.keys(data).length;
        console.log('[RTDB-DEBUG] Found', count, 'notifications in database for user', userID);
        console.log('[RTDB-DEBUG] First few notification keys:', Object.keys(data).slice(0, 3));
      } else {
        console.log('[RTDB-DEBUG] No notifications found in database for user', userID);
      }
      // Unsubscribe after first read to avoid duplicate listeners
      unsubscribe();
    }, (error: any) => {
      console.error('[RTDB-DEBUG] Error reading notifications:', error);
      console.error('[RTDB-DEBUG] Error code:', error?.code);
      console.error('[RTDB-DEBUG] Error message:', error?.message);
    });
    console.log('[RTDB-DEBUG] onValue listener registered');
    
    this.notificationsWatcher = onChildAdded(myNotificationsRef, (snapshot) => {
      const nowDate = new Date();
      console.log('[RTDB-DEBUG] onChildAdded fired! New notification received:', snapshot.key);
      console.log('[RTDB-DEBUG] Notification data:', snapshot.val());
      let data = snapshot.val();
      let duration = humanizeDuration(nowDate.getTime() - data.updateTimestamp);
      let newEntry: any = Object.assign({}, data, {
        key: snapshot.key,
        duration: duration,
        isRead: data.isRead ?? false,
        readAt: data.readAt,
      });
      this.myNotifications.push(newEntry);
      this.updateUnreadCount();
      this.alertNotification();
      if (data.status == 'new') {
        this.markAsReceived(userID, snapshot.key);
      }
    });

    // Listen for removed notifications
    this.notificationsRemovedWatcher = onChildRemoved(myNotificationsRef, (snapshot) => {
      console.log('notification removed from firebase', snapshot.key);
      // Remove from local array
      this.myNotifications = this.myNotifications.filter((entry) => {
        return entry.key !== snapshot.key;
      });
      this.updateUnreadCount();
    });

    // Listen for changed notifications (e.g., marked as read)
    this.notificationsChangedWatcher = onChildChanged(myNotificationsRef, (snapshot) => {
      console.log('notification changed in firebase', snapshot.key);
      const data = snapshot.val();
      const index = this.myNotifications.findIndex(n => n.key === snapshot.key);
      if (index !== -1) {
        // Update the notification in the local array
        const nowDate = new Date();
        let duration = humanizeDuration(nowDate.getTime() - data.updateTimestamp);
        this.myNotifications[index] = {
          ...data,
          key: snapshot.key,
          duration: duration,
          isRead: data.isRead ?? false,
          readAt: data.readAt,
        };
        this.updateUnreadCount();
      }
    });

    setTimeout(() => {
      firstTime = false;
    }, 3000);
  }

  unreadMyNotifications(userID: string) {
    if (this.notificationsWatcher !== undefined) {
      this.notificationsWatcher();
    }
    if (this.notificationsRemovedWatcher !== undefined) {
      this.notificationsRemovedWatcher();
    }
    if (this.notificationsChangedWatcher !== undefined) {
      this.notificationsChangedWatcher();
    }
    this.myNotifications = [];
    this.unreadCount = 0;
  }

  deleteNotification(userID: string, key: string) {
    console.log('delete notification', userID, key);
    
    // First, remove from local array to ensure immediate UI update
    const index = this.myNotifications.findIndex(entry => entry.key === key);
    if (index !== -1) {
      this.myNotifications.splice(index, 1);
      this.updateUnreadCount();
    }
    
    // Then remove from Firebase
    const myNotificationRef = ref(
      this.rtdb,
      'notifications/' + userID + '/' + key
    );
    remove(myNotificationRef).catch(error => {
      console.error('Error deleting notification from Firebase:', error);
      // If deletion fails, we might want to re-add to local array
      // but for now, we'll just log the error
    });
  }

  alertNotification() {
    // okToDing is a flag to prevent the alert from being triggered too frequently

    if (this.okToDing) {
      this.playBellDing();
      // setTimeout(() => {
      //   alert('New notifications received');
      // }, 2000);
      this.okToDing = false;
      setTimeout(() => {
        this.okToDing = true; // ok to ding again after 5 minutes
      }, 5 * 60 * 1000);
    }
  }

  // set the status of a notification to "received"
  markAsReceived(userID: string, key: string | null) {
    console.log('mark as received', userID, key);
    if (key === null) {
      return;
    }
    const myNotificationRef = ref(
      this.rtdb,
      'notifications/' + userID + '/' + key
    );
    update(myNotificationRef, { status: 'received' });
  }

  // mark a notification as read
  markAsRead(userID: string, key: string | null) {
    console.log('mark as read', userID, key);
    if (key === null) {
      return;
    }
    const myNotificationRef = ref(
      this.rtdb,
      'notifications/' + userID + '/' + key
    );
    const readAt = new Date().getTime();
    update(myNotificationRef, { isRead: true, readAt: readAt });
    
    // Update local array
    const notification = this.myNotifications.find(n => n.key === key);
    if (notification) {
      notification.isRead = true;
      notification.readAt = readAt;
      this.updateUnreadCount();
    }
  }

  // update the unread count
  updateUnreadCount() {
    this.unreadCount = this.myNotifications.filter(n => !n.isRead).length;
  }

  // get all notifications with pagination for history
  getNotificationHistory(userID: string, limit: number = 20, startAfter?: string) {
    const myNotificationsRef = ref(this.rtdb, 'notifications/' + userID);
    let historyQuery;
    
    if (startAfter) {
      historyQuery = query(
        myNotificationsRef,
        orderByKey(),
        firebaseStartAfter(startAfter),
        limitToFirst(limit)
      );
    } else {
      historyQuery = query(
        myNotificationsRef,
        orderByKey(),
        limitToLast(limit)
      );
    }
    
    return new Promise((resolve, reject) => {
      onValue(historyQuery, (snapshot) => {
        const notifications: Notifications[] = [];
        snapshot.forEach((childSnapshot) => {
          const data = childSnapshot.val();
          const nowDate = new Date();
          let duration = humanizeDuration(nowDate.getTime() - data.updateTimestamp);
          
          notifications.push({
            ...data,
            key: childSnapshot.key,
            duration: duration,
            isRead: data.isRead ?? false,
            readAt: data.readAt
          });
        });
        
        // Sort by timestamp descending (newest first)
        notifications.sort((a, b) => b.updateTimestamp - a.updateTimestamp);
        resolve(notifications);
      }, reject);
    });
  }

  playBellDing() {
    const audio = new Audio();
    audio.src = 'assets/audio/bell-ding.mp3';
    audio.load();
    audio.play();
  }

  playClosedDeal() {
    const audio = new Audio();
    audio.src = 'assets/audio/closed-deal.mp3';
    audio.load();
    audio.play();
  }

  updateDuration() {
    const nowDate = new Date();
    this.myNotifications.forEach((entry) => {
      console.log('update duration', nowDate, entry.updateTimestamp);
      let duration = humanizeDuration(
        nowDate.getTime() - entry.updateTimestamp
      );
      entry.duration = duration;
    });
  }
}
